/**
 * Identity claims on an Arcane- or Cloud-issued token, and how Arcane resolves them.
 *
 * Three token shapes exist, and the claims present decide which one this is. A token is
 * read as wholly one shape — never a mix:
 *
 * 1. **Cloud-issued, legacy organization.** Legacy organizations predate the migration
 *    and exist in both Auth0 tenants. Arcane keys their stored data on the *Arcane*
 *    tenant's ids, which the Cloud post-login Action copies through as namespaced
 *    bridge claims (CLOUD-726).
 * 2. **Cloud-issued, Cloud-native organization.** Created after the migration, exists
 *    only in Cloud, carries no Arcane claims; its identity is the governance one.
 * 3. **Arcane-issued.** Carries neither, because the Arcane-tenant post-login Action
 *    stamps email/name/roles/org_features but has never stamped an `org_id` claim.
 *    Here the native `org_id` is the Arcane organization (ARP-3487).
 *
 * Shape 3 is every environment not yet cut over to the Cloud tenant — staging and
 * production today. Shapes 1 and 3 disappear once all organizations are re-keyed and
 * all environments are on Cloud.
 */
export const ARCANE_NAMESPACE = 'https://arcane.anthill.com';
export const CLOUD_NAMESPACE = 'https://cloud.anthill.technology/claims/';

/**
 * Read one Arcane bridge claim off an Auth0 user profile.
 * @param {object} user - the profile from useAuth0().user
 * @param {string} name - claim name without the namespace, e.g. 'roles'
 * @returns {*} the claim value, or undefined when absent
 */
export const getArcaneClaim = (user, name) => user?.[`${ARCANE_NAMESPACE}/${name}`];

/**
 * Read one Cloud governance claim off an Auth0 user profile.
 * @param {object} user - the profile from useAuth0().user
 * @param {string} name - claim name without the namespace, e.g. 'cloudUserId'
 * @returns {*} the claim value, or undefined when absent
 */
export const getCloudClaim = (user, name) => user?.[`${CLOUD_NAMESPACE}${name}`];

/**
 * True when the token belongs to a legacy organization — one that still carries the
 * Arcane bridge claims. Decides which identity the two resolvers below return.
 * @param {object} user - the profile from useAuth0().user
 * @returns {boolean}
 */
const isLegacyOrganization = (user) => Boolean(getArcaneClaim(user, 'org_id'));

/**
 * The organization id Arcane scopes its data by: the Arcane organization for a legacy
 * organization, the governance organization for a Cloud-native one, and the native
 * `org_id` on an Arcane-issued token.
 * @param {object} user - the profile from useAuth0().user
 * @returns {string|null} the id, or null when none of the three is present
 */
export const getOrgId = (user) => {
  if (isLegacyOrganization(user)) {
    // Auth0 prefixes its own organization ids; Arcane stores them without it.
    return getArcaneClaim(user, 'org_id').replace(/^org_/, '');
  }
  const cloudOrgId = getCloudClaim(user, 'cloudOrganizationId');
  if (cloudOrgId) return cloudOrgId;
  // Arcane-issued (ARP-3487): no bridge claim and no governance claim, so the native
  // `org_id` is the Arcane organization. See the note above getUserId.
  return user?.org_id?.replace(/^org_/, '') ?? null;
};

/**
 * The user id Arcane keys its data by.
 *
 * For a legacy organization and for an Arcane-issued token alike this is the trailing
 * segment of `sub`, which survives federation unchanged; for a Cloud-native
 * organization it is the governance user id.
 *
 * An Arcane-issued token (ARP-3487) carries neither the bridge claim nor the governance
 * claims, because only the Cloud post-login Action stamps them — the Arcane-tenant
 * Action has never stamped an `org_id` claim. Staging and production still issue such
 * tokens, and without this fallback they resolve no identity at all.
 *
 * @param {object} user - the profile from useAuth0().user
 * @returns {string|null} the id, or null when it cannot be resolved
 */
export const getUserId = (user) => {
  const subTail = () => user.sub.split('|').splice(-1)[0].replaceAll('.', '-').replaceAll('@', '-');
  if (isLegacyOrganization(user)) return subTail();
  const cloudUserId = getCloudClaim(user, 'cloudUserId');
  if (cloudUserId) return cloudUserId;
  return user?.sub ? subTail() : null;
};
